import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { EmailTagInput } from '@/components/ui/email-tag-input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Mail, MessageSquare, Send, AlertCircle, Check, UserPlus } from 'lucide-react';
import { PreApprovalData, PreApprovalRecipient } from '@/types/pre-approval';
import { EmailService } from '@/services';
import { SmsService } from '@/services/smsService';
import { SupabasePreApprovalService } from '@/services/supabasePreApprovalService';
import { RecipientAccessService } from '@/services/recipientAccessService';
import { PRE_APPROVAL_CONSTANTS } from '@/constants/pre-approval';
import { useAuth } from '@/contexts/useAuth';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import { partnerContactService, PartnerContact } from '@/services/partnerContactService';
import { ContactService } from '@/services/contactService-new';
import { QuickAddPartnerModal } from '@/components/contacts/QuickAddPartnerModal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { formatPercentage, formatPhoneNumber } from '@/utils/formatCurrency';
import { formatLoanType } from '@/utils/pre-approval/formatters';
import { capitalizeName } from '@/utils/pre-approval/greetingHelpers';
import {
  formatPreApprovalExpirationDate,
  getPreApprovalExpirationInstant,
  resolveCanonicalPreApprovalExpiresAt
} from '@/utils/pre-approval/expiration';

const contactService = new ContactService();

const log = createComponentLogger('PreApprovalSendModal', 'pre-approval');

/** Lightweight type for co-borrower dropdown entries — avoids coupling to full Contact model */
interface RecipientOption {
  id: string;
  firstName: string;
  lastName: string;
  salutation?: string;
  email?: string;
  mobilePhone?: string;
  source: 'contact' | 'pre_approval';
}

/** Normalize phone to last 10 digits for comparison */
const normalizeLast10 = (phone: string): string => phone.replace(/\D/g, '').slice(-10);

const formatPartnerType = (type: string): string => {
  const typeMap: Record<string, string> = {
    realtor: 'Realtor',
    financial_advisor: 'Financial Advisor',
    insurance_agent: 'Insurance Agent',
    attorney: 'Attorney',
    cpa: 'CPA',
    other: 'Partner'
  };
  return typeMap[type] || 'Partner';
};

type SendPhase = 'editing' | 'sending' | 'sent' | 'failed';
type SendRecipientType = 'borrower' | 'co_borrower' | 'partner' | 'loan_officer';
type SendChannel = 'email' | 'sms' | 'confirmation';
type SendResultStatus = 'success' | 'failed';

interface RecipientSendResult {
  recipientType: SendRecipientType;
  recipientName: string;
  channel: SendChannel;
  destination: string;
  status: SendResultStatus;
  accessUrl?: string;
  error?: string;
}

const getErrorMessage = (error: unknown, fallback = 'Unknown error'): string =>
  error instanceof Error ? error.message : String(error || fallback);

const formatGreeting = (borrowerInfo?: Partial<PreApprovalData['borrowerInfo']>) => {
  if (!borrowerInfo) {
    return 'Dear [Borrower Name]';
  }

  if (borrowerInfo.salutation && borrowerInfo.salutation !== 'none' && borrowerInfo.lastName) {
    return `Dear ${borrowerInfo.salutation} ${capitalizeName(borrowerInfo.lastName)}`;
  }

  const firstName = borrowerInfo.firstName || '[Borrower Name]';
  const formattedFirstName = capitalizeName(firstName);
  return `Dear ${formattedFirstName}`;
};

const buildLinkExpirationCopy = (preApproval: PreApprovalData): string => {
  const expirationDate = formatPreApprovalExpirationDate(
    resolveCanonicalPreApprovalExpiresAt(preApproval.expiresAt, preApproval.expirationDate)
  );

  return expirationDate
    ? `The link expires on ${expirationDate}.`
    : 'Please contact me if the link does not open.';
};

const buildBorrowerMessage = (
  preApproval: PreApprovalData,
  loanOfficerName: string
) => `${formatGreeting(preApproval?.borrowerInfo)},\n\nI'm pleased to provide your pre-approval letter for your home purchase. This letter confirms your pre-approval for a mortgage loan up to $${preApproval?.loanDetails?.loanAmount?.toLocaleString() || '0'}.\n\nYou can access your pre-approval letter and make any necessary updates using the secure link below. ${buildLinkExpirationCopy(preApproval)}\n\nIf you have any questions or need assistance, please don't hesitate to contact me.\n\nBest regards,\n${loanOfficerName}`;

const buildCoBorrowerMessage = (
  preApproval: PreApprovalData,
  coBorrower: RecipientOption | null,
  loanOfficerName: string
) => `${formatGreeting(coBorrower || preApproval?.coBorrowerInfo)},\n\nI'm pleased to provide the pre-approval letter for your home purchase. This letter confirms the pre-approval for a mortgage loan up to $${preApproval?.loanDetails?.loanAmount?.toLocaleString() || '0'}.\n\nYou can access the pre-approval letter using the secure link below. ${buildLinkExpirationCopy(preApproval)}\n\nIf you have any questions or need assistance, please don't hesitate to contact me.\n\nBest regards,\n${loanOfficerName}`;

const buildPartnerMessage = (
  preApproval: PreApprovalData,
  partner: PartnerContact | null,
  loanOfficerName: string,
  loanOfficerTitle?: string,
  loanOfficerPhone?: string
) => `Hi ${partner?.firstName || '[Partner Name]'},\n\nI'm excited to share the pre-approval letter for our mutual client, ${preApproval?.borrowerInfo?.firstName || '[Borrower]'} ${preApproval?.borrowerInfo?.lastName || '[Last Name]'}.\n\nThey are pre-approved for up to $${preApproval?.loanDetails?.loanAmount?.toLocaleString() || '0'} and ready to make competitive offers on properties.\n\nKey Details:\n• Loan Amount: $${preApproval?.loanDetails?.loanAmount?.toLocaleString() || '0'}\n• Purchase Price: Up to $${preApproval?.loanDetails?.purchasePrice?.toLocaleString() || '0'}\n• Down Payment: ${(preApproval?.loanDetails?.downPaymentPercent || 0).toFixed(1)}%\n• Loan Type: ${formatLoanType(preApproval?.loanDetails?.loanType || 'N/A')}\n\nPlease feel free to reach out with any questions. I look forward to working together on this transaction.\n\nBest regards,\n${loanOfficerName}\n${loanOfficerTitle || ''}\n${loanOfficerPhone || ''}`;

interface PreApprovalSendModalProps {
  isOpen: boolean;
  onClose: () => void;
  preApproval: PreApprovalData;
  onSend: () => void;
  isResend?: boolean;
}

export const PreApprovalSendModal: React.FC<PreApprovalSendModalProps> = ({
  isOpen,
  onClose,
  preApproval,
  onSend,
  isResend = false
}) => {
  const { user } = useAuth();

  // Wizard step state
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);

  // Step 1: Recipient selection
  const [sendToPartner, setSendToPartner] = useState(false);
  const [partners, setPartners] = useState<PartnerContact[]>([]);
  const [selectedPartners, setSelectedPartners] = useState<PartnerContact[]>([]);

  const [sendToCoBorrower, setSendToCoBorrower] = useState(false);
  const [borrowers, setBorrowers] = useState<RecipientOption[]>([]);
  const [selectedCoBorrower, setSelectedCoBorrower] = useState<RecipientOption | null>(null);

  // Quick add partner modal
  const [showQuickAddPartner, setShowQuickAddPartner] = useState(false);

  // Step 2: Delivery method
  const [deliveryMethod, setDeliveryMethod] = useState<'email' | 'sms' | 'both'>('email');
  const [partnerDeliveryMethods, setPartnerDeliveryMethods] = useState<Record<string, 'email' | 'sms' | 'both'>>({});
  const [coBorrowerDeliveryMethod, setCoBorrowerDeliveryMethod] = useState<'email' | 'sms' | 'both'>('email');
  const recipientEmail = preApproval?.borrowerInfo?.email || '';
  const recipientPhone = preApproval?.borrowerInfo?.phone || '';

  // Step 3: Messages and consent
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [smsConsent, setSmsConsent] = useState(false);
  const [smsConsentManuallyEdited, setSmsConsentManuallyEdited] = useState(false);
  const [sendPhase, setSendPhase] = useState<SendPhase>('editing');
  const [sendResults, setSendResults] = useState<RecipientSendResult[]>([]);
  const [isStep3SendGuardActive, setIsStep3SendGuardActive] = useState(false);

  // Inline partner contact editing (single target — null when no partner is being edited)
  const [editingPartnerId, setEditingPartnerId] = useState<string | null>(null);
  const [editPartnerEmail, setEditPartnerEmail] = useState('');
  const [editPartnerPhone, setEditPartnerPhone] = useState('');
  const [isSavingPartnerContact, setIsSavingPartnerContact] = useState(false);

  // Get loan officer name for messages
  const loanOfficerName = user?.fullName || (
    user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : 'Your Loan Officer'
  );
  const loanOfficerTitle = user?.title || '';
  const loanOfficerPhone = user?.mobilePhone || '';

  const [message, setMessage] = useState(() => buildBorrowerMessage(preApproval, loanOfficerName));
  const [coBorrowerMessage, setCoBorrowerMessage] = useState(() => buildCoBorrowerMessage(preApproval, null, loanOfficerName));
  const [partnerMessages, setPartnerMessages] = useState<Record<string, string>>({});
  const [dirtyMessages, setDirtyMessages] = useState({
    borrower: false,
    coBorrower: false
  });
  const [dirtyPartnerMessageIds, setDirtyPartnerMessageIds] = useState<Set<string>>(new Set());

  // Master-checkbox guard: when sendToPartner is unchecked, selectedPartners should not contribute to sends.
  const activeSelectedPartners = useMemo(
    () => (sendToPartner ? selectedPartners : []),
    [sendToPartner, selectedPartners]
  );

  const latestOnCloseRef = useRef(onClose);
  const latestPreApprovalRef = useRef(preApproval);
  const latestMessageDefaultsRef = useRef({
    loanOfficerName,
    loanOfficerTitle,
    loanOfficerPhone
  });
  const openTransitionRef = useRef<{ wasOpen: boolean; preApprovalId: string | null }>({
    wasOpen: false,
    preApprovalId: null
  });
  const sendLockRef = useRef(false);
  const step3GuardTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const smsConsentManuallyEditedRef = useRef(false);
  const lastConsentPhoneSetKeyRef = useRef<string>('');
  const consentLookupReqRef = useRef(0);

  useEffect(() => {
    smsConsentManuallyEditedRef.current = smsConsentManuallyEdited;
  }, [smsConsentManuallyEdited]);

  const phoneSetKey = (phones: string[]) => [...phones].sort().join('|');

  const computeCurrentPhones = useCallback((coBorrower: RecipientOption | null | undefined): string[] => {
    const phones: string[] = [];
    if (deliveryMethod === 'sms' || deliveryMethod === 'both') {
      if (recipientPhone) {
        phones.push(SmsService.formatToE164(recipientPhone));
      }
    }
    if (sendToCoBorrower && (coBorrowerDeliveryMethod === 'sms' || coBorrowerDeliveryMethod === 'both')) {
      if (coBorrower?.mobilePhone) {
        phones.push(SmsService.formatToE164(coBorrower.mobilePhone));
      }
    }
    return phones;
  }, [deliveryMethod, recipientPhone, sendToCoBorrower, coBorrowerDeliveryMethod]);

  const runConsentPrefill = useCallback(async (opts: {
    ignoreManualLock: boolean;
    coBorrower?: RecipientOption | null;
    currentPhones: string[];
  }) => {
    if (!opts.ignoreManualLock && smsConsentManuallyEditedRef.current) {
      return;
    }
    const myReq = ++consentLookupReqRef.current;

    const uniquePhones = Array.from(new Set(opts.currentPhones));

    if (uniquePhones.length === 0) {
      if (myReq !== consentLookupReqRef.current) {
        return;
      }
      if (!smsConsentManuallyEditedRef.current) {
        setSmsConsent(false);
      }
      lastConsentPhoneSetKeyRef.current = phoneSetKey(uniquePhones);
      return;
    }
    const results = await Promise.all(uniquePhones.map(p => SmsService.checkSmsConsentByPhone(p)));
    if (myReq !== consentLookupReqRef.current) {
      return;
    }
    // Record the evaluated phone-set key regardless of whether the user took control mid-lookup.
    // Otherwise the next same-set effect re-fire would see a stale key and falsely declare
    // "phone-set changed", wiping the user's manual choice.
    lastConsentPhoneSetKeyRef.current = phoneSetKey(uniquePhones);
    if (smsConsentManuallyEditedRef.current) {
      return;
    }

    setSmsConsent(results.every(r => r.granted));
  }, []);

  const addSelectedPartner = useCallback((partner: PartnerContact) => {
    setSelectedPartners(prev => prev.some(p => p.id === partner.id) ? prev : [...prev, partner]);
    setPartnerDeliveryMethods(prev => {
      if (prev[partner.id]) {
        return prev;
      }
      const hasEmail = Boolean(partner.email);
      const hasPhone = Boolean(partner.mobilePhone);
      const defaultMethod: 'email' | 'sms' | 'both' =
        hasEmail && !hasPhone ? 'email' :
        !hasEmail && hasPhone ? 'sms' :
        'email';
      return { ...prev, [partner.id]: defaultMethod };
    });
    setPartnerMessages(prev => {
      if (prev[partner.id] && dirtyPartnerMessageIds.has(partner.id)) {
        return prev;
      }
      return {
        ...prev,
        [partner.id]: buildPartnerMessage(preApproval, partner, loanOfficerName, loanOfficerTitle, loanOfficerPhone)
      };
    });
  }, [preApproval, loanOfficerName, loanOfficerTitle, loanOfficerPhone, dirtyPartnerMessageIds]);

  const removeSelectedPartner = useCallback((partnerId: string) => {
    setSelectedPartners(prev => prev.filter(p => p.id !== partnerId));
    setPartnerDeliveryMethods(prev => {
      const next = { ...prev };
      delete next[partnerId];
      return next;
    });
    setPartnerMessages(prev => {
      const next = { ...prev };
      delete next[partnerId];
      return next;
    });
    setDirtyPartnerMessageIds(prev => {
      const next = new Set(prev);
      next.delete(partnerId);
      return next;
    });
    setEditingPartnerId(prev => (prev === partnerId ? null : prev));
  }, []);

  latestOnCloseRef.current = onClose;
  latestPreApprovalRef.current = preApproval;
  latestMessageDefaultsRef.current = {
    loanOfficerName,
    loanOfficerTitle,
    loanOfficerPhone
  };

  const isSending = sendPhase === 'sending';
  const isResultPhase = sendPhase === 'sent' || sendPhase === 'failed';
  const isEditingDisabled = isSending || isResultPhase;

  const recipientReviewItems = useMemo(() => {
    const items: Array<{
      key: string;
      role: string;
      name: string;
      methods: string[];
    }> = [];

    const borrowerMethods: string[] = [];
    if (deliveryMethod === 'email' || deliveryMethod === 'both') {
      borrowerMethods.push(recipientEmail);
    }
    if (deliveryMethod === 'sms' || deliveryMethod === 'both') {
      borrowerMethods.push(formatPhoneNumber(recipientPhone));
    }

    items.push({
      key: 'borrower',
      role: 'Borrower',
      name: `${preApproval.borrowerInfo.firstName} ${preApproval.borrowerInfo.lastName}`.trim(),
      methods: borrowerMethods.filter(Boolean)
    });

    if (sendToCoBorrower && selectedCoBorrower) {
      const coBorrowerMethods: string[] = [];
      if ((coBorrowerDeliveryMethod === 'email' || coBorrowerDeliveryMethod === 'both') && selectedCoBorrower.email) {
        coBorrowerMethods.push(selectedCoBorrower.email);
      }
      if ((coBorrowerDeliveryMethod === 'sms' || coBorrowerDeliveryMethod === 'both') && selectedCoBorrower.mobilePhone) {
        coBorrowerMethods.push(formatPhoneNumber(selectedCoBorrower.mobilePhone));
      }
      items.push({
        key: 'co-borrower',
        role: 'Co-Borrower',
        name: `${selectedCoBorrower.firstName} ${selectedCoBorrower.lastName}`.trim(),
        methods: coBorrowerMethods
      });
    }

    activeSelectedPartners.forEach(p => {
      const method = partnerDeliveryMethods[p.id];
      const partnerMethods: string[] = [];
      if ((method === 'email' || method === 'both') && p.email) {
        partnerMethods.push(p.email);
      }
      if ((method === 'sms' || method === 'both') && p.mobilePhone) {
        partnerMethods.push(formatPhoneNumber(p.mobilePhone));
      }
      items.push({
        key: `partner-${p.id}`,
        role: formatPartnerType(p.partnerType),
        name: `${p.firstName} ${p.lastName}`.trim(),
        methods: partnerMethods
      });
    });

    return items;
  }, [
    deliveryMethod,
    coBorrowerDeliveryMethod,
    partnerDeliveryMethods,
    preApproval.borrowerInfo.firstName,
    preApproval.borrowerInfo.lastName,
    recipientEmail,
    recipientPhone,
    selectedCoBorrower,
    activeSelectedPartners,
    sendToCoBorrower
  ]);

  // Reusable partner loading function (called on open + after quick-add)
  const loadPartners = async () => {
    try {
      const partnerList = await partnerContactService.list({
        partnerStatus: 'active'
      });
      setPartners(partnerList);
      return partnerList;
    } catch (error) {
      log.error('Failed to load partners', {}, error as Error);
      return [];
    }
  };

  // Load partner contacts and borrowers only when the modal actually opens or swaps IDs.
  useEffect(() => {
    const previousOpenState = openTransitionRef.current;
    const currentPreApprovalId = preApproval?.id || null;
    const shouldInitialize =
      isOpen &&
      (!previousOpenState.wasOpen || previousOpenState.preApprovalId !== currentPreApprovalId);

    openTransitionRef.current = {
      wasOpen: isOpen,
      preApprovalId: isOpen ? currentPreApprovalId : null
    };

    if (!shouldInitialize) {
      return;
    }

    const currentPreApproval = latestPreApprovalRef.current;
    if (!currentPreApproval) {
      return;
    }
    const messageDefaults = latestMessageDefaultsRef.current;

    // Validate signature requirement before loading modal
    const templateRequiresSignature = currentPreApproval?.template_data?.elements?.some(
      element => element.type === 'signature' ||
                 (element.content && element.content.includes('{{loanOfficerSignature}}'))
    );

    if (templateRequiresSignature && !currentPreApproval?.hasLoanOfficerSignature) {
      toast.error('Pre-approval must be signed before sending', {
        description: 'Please e-sign the pre-approval in the editor first'
      });
      latestOnCloseRef.current();
      return;
    }

    let isActive = true;

    const resetSendGuard = () => {
      if (step3GuardTimerRef.current) {
        clearTimeout(step3GuardTimerRef.current);
        step3GuardTimerRef.current = null;
      }
      setIsStep3SendGuardActive(false);
    };

    // Reset wizard on true open or pre-approval ID swap.
    setCurrentStep(1);
    setSendToPartner(false);
    setSelectedPartners([]);
    setPartnerDeliveryMethods({});
    setPartnerMessages({});
    setDirtyPartnerMessageIds(new Set());
    setEditingPartnerId(null);
    setSendToCoBorrower(false);
    setSelectedCoBorrower(null);
    setDeliveryMethod('email');
    setCoBorrowerDeliveryMethod('email');
    setCcEmails([]);
    setSmsConsent(false);
    setSmsConsentManuallyEdited(false);
    smsConsentManuallyEditedRef.current = false;
    lastConsentPhoneSetKeyRef.current = '';
    consentLookupReqRef.current++;
    setShowQuickAddPartner(false);
    setEditPartnerEmail('');
    setEditPartnerPhone('');
    setSendPhase('editing');
    setSendResults([]);
    setDirtyMessages({ borrower: false, coBorrower: false });
    setMessage(buildBorrowerMessage(currentPreApproval, messageDefaults.loanOfficerName));
    setCoBorrowerMessage(buildCoBorrowerMessage(currentPreApproval, null, messageDefaults.loanOfficerName));
    sendLockRef.current = false;
    resetSendGuard();

    const loadContacts = async () => {
      try {
        // Load partners
        await loadPartners();

        // Load borrower contacts (excluding current borrower)
        const borrowerList = await contactService.list({
          contactType: 'borrower',
          limit: 100
        });

        if (!isActive) {
          return;
        }

        // Filter out the current borrower and map to RecipientOption
        const mapped: RecipientOption[] = borrowerList
          .filter(b => b.email !== currentPreApproval?.borrowerInfo?.email)
          .map(b => ({
            id: b.id,
            firstName: b.firstName,
            lastName: b.lastName,
            salutation: b.salutation,
            email: b.email,
            mobilePhone: b.mobilePhone,
            source: 'contact' as const
          }));

        // If pre-approval has co-borrower info, try to match or synthesize
        const coInfo = currentPreApproval?.coBorrowerInfo;
        let matchedCoBorrower: RecipientOption | null = null;

        if (coInfo?.firstName && coInfo?.lastName) {
          // Cascading match: email → phone → name
          const coEmail = coInfo.email?.trim().toLowerCase();
          const coPhone = coInfo.phone?.trim();
          const coFirst = coInfo.firstName.trim().toLowerCase();
          const coLast = coInfo.lastName.trim().toLowerCase();

          matchedCoBorrower =
            // 1. Email match (if non-empty)
            (coEmail && mapped.find(b => b.email?.toLowerCase() === coEmail)) ||
            // 2. Phone match (if non-empty, normalized to last 10 digits)
            (coPhone && coPhone.replace(/\D/g, '').length >= 10 &&
              mapped.find(b => b.mobilePhone && normalizeLast10(b.mobilePhone) === normalizeLast10(coPhone))) ||
            // 3. Exact name match (case-insensitive)
            mapped.find(b =>
              b.firstName.trim().toLowerCase() === coFirst &&
              b.lastName.trim().toLowerCase() === coLast
            ) ||
            null;

          // If DB match found, enrich with pre-approval data where DB is missing values
          // (contactService-new.ts:mapToContact does not map the salutation column)
          if (matchedCoBorrower && matchedCoBorrower.source !== 'pre_approval') {
            if (coInfo.salutation) {
              matchedCoBorrower.salutation = matchedCoBorrower.salutation || coInfo.salutation;
            }
            if (coEmail && !matchedCoBorrower.email) {
              matchedCoBorrower.email = coInfo.email;
            }
            if (coPhone && !matchedCoBorrower.mobilePhone) {
              matchedCoBorrower.mobilePhone = coInfo.phone;
            }
          }

          // If no match found, synthesize from pre-approval data
          if (!matchedCoBorrower) {
            const synthetic: RecipientOption = {
              id: `pa-coborrower-${currentPreApproval.id}`,
              firstName: coInfo.firstName,
              lastName: coInfo.lastName,
              salutation: coInfo.salutation || undefined,
              email: coInfo.email || undefined,
              mobilePhone: coInfo.phone || undefined,
              source: 'pre_approval'
            };
            mapped.unshift(synthetic);
            matchedCoBorrower = synthetic;
          }
        }

        if (!isActive) {
          return;
        }

        setBorrowers(mapped);

        // Auto-select co-borrower if deliverable (has email OR phone — co-borrower now supports SMS)
        if (matchedCoBorrower?.email || matchedCoBorrower?.mobilePhone) {
          const hasEmail = Boolean(matchedCoBorrower.email);
          const hasPhone = Boolean(matchedCoBorrower.mobilePhone);
          const defaultMethod: 'email' | 'sms' | 'both' =
            hasEmail && !hasPhone ? 'email' :
            !hasEmail && hasPhone ? 'sms' :
            'email';
          setSendToCoBorrower(true);
          setSelectedCoBorrower(matchedCoBorrower);
          setCoBorrowerDeliveryMethod(defaultMethod);
          setCoBorrowerMessage(buildCoBorrowerMessage(
            currentPreApproval,
            matchedCoBorrower,
            messageDefaults.loanOfficerName
          ));

          // Kick off consent prefill for the auto-selected co-borrower (manual lock is fresh after reset).
          const currentPhones: string[] = [];
          if (recipientPhone) {
            currentPhones.push(SmsService.formatToE164(recipientPhone));
          }
          if ((defaultMethod === 'sms' || defaultMethod === 'both') && matchedCoBorrower.mobilePhone) {
            currentPhones.push(SmsService.formatToE164(matchedCoBorrower.mobilePhone));
          }
          runConsentPrefill({ ignoreManualLock: true, coBorrower: matchedCoBorrower, currentPhones });
        } else {
          // Even without co-borrower, prime the borrower-only consent prefill.
          // (Borrower phone consent only matters if delivery method is SMS, but we still prime so the effect's
          // lastConsentPhoneSetKeyRef has an initial value.)
          runConsentPrefill({ ignoreManualLock: true, coBorrower: null, currentPhones: [] });
        }
      } catch (error) {
        log.error('Failed to load contacts', {}, error as Error);
      }
    };

    loadContacts();

    return () => {
      isActive = false;
    };
  // recipientPhone + runConsentPrefill are stable (prop-derived string + useCallback with empty deps);
  // including them satisfies exhaustive-deps without causing re-fires beyond the intended open/swap trigger.
  }, [isOpen, preApproval?.id, recipientPhone, runConsentPrefill]);

  useEffect(() => () => {
    if (step3GuardTimerRef.current) {
      clearTimeout(step3GuardTimerRef.current);
    }
  }, []);

  // Re-run consent prefill when any relevant dependency changes. The helper handles
  // the manual-edit lock + stale-request cancellation internally.
  useEffect(() => {
    const currentPhones = computeCurrentPhones(selectedCoBorrower);
    const newKey = phoneSetKey(Array.from(new Set(currentPhones)));
    const phoneSetChanged = newKey !== lastConsentPhoneSetKeyRef.current;
    if (phoneSetChanged) {
      // Stamp the key BEFORE the async lookup so a same-set effect re-fire that
      // arrives while runConsentPrefill is in flight does not see a stale key
      // and falsely declare "phone set changed" again (which would wipe the
      // user's manual checkbox choice).
      lastConsentPhoneSetKeyRef.current = newKey;
      smsConsentManuallyEditedRef.current = false;
      setSmsConsentManuallyEdited(false);
    }
    runConsentPrefill({ ignoreManualLock: phoneSetChanged, coBorrower: selectedCoBorrower, currentPhones });
  }, [
    recipientPhone,
    selectedCoBorrower,
    deliveryMethod,
    coBorrowerDeliveryMethod,
    sendToCoBorrower,
    computeCurrentPhones,
    runConsentPrefill
  ]);

  // Reset edit fields when the in-flight partner-edit target changes
  useEffect(() => {
    setEditPartnerEmail('');
    setEditPartnerPhone('');
  }, [editingPartnerId]);

  // Co-borrower auto-select delivery method based on available contact info (mirror of partner pattern).
  useEffect(() => {
    if (!selectedCoBorrower || !sendToCoBorrower) {
      return;
    }
    const hasEmail = Boolean(selectedCoBorrower.email);
    const hasPhone = Boolean(selectedCoBorrower.mobilePhone);
    if (hasEmail && !hasPhone) {
      setCoBorrowerDeliveryMethod('email');
    } else if (!hasEmail && hasPhone) {
      setCoBorrowerDeliveryMethod('sms');
    }
    // both present: leave current value (likely 'email' from initial pick) so the user can switch
  }, [selectedCoBorrower, sendToCoBorrower]);

  // Guard against invalid data
  if (!preApproval || !preApproval.borrowerInfo || !preApproval.loanDetails) {
    return null;
  }

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^\+?1?\d{10,14}$/;
    return phoneRegex.test(phone.replace(/\D/g, ''));
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1: {
        // If partner master checkbox is on, must choose at least one partner
        if (sendToPartner && activeSelectedPartners.length === 0) {
          toast.error('Please select at least one partner/realtor');
          return false;
        }

        // Validate each selected partner has at least one contact method
        for (const p of activeSelectedPartners) {
          if (!p.email && !p.mobilePhone) {
            toast.error(`${p.firstName} ${p.lastName} has no email or phone on file`, {
              description: 'Please update their contact information before sending'
            });
            return false;
          }
        }

        // Validate co-borrower selection
        if (sendToCoBorrower && !selectedCoBorrower) {
          toast.error('Please select a co-borrower');
          return false;
        }
        // Co-borrower must have email OR phone (SMS now supported)
        if (sendToCoBorrower && selectedCoBorrower && !selectedCoBorrower.email && !selectedCoBorrower.mobilePhone) {
          toast.error('Selected co-borrower must have at least an email or phone number');
          return false;
        }
        return true;
      }

      case 2: {
        // Must have contact info for selected delivery method
        if ((deliveryMethod === 'email' || deliveryMethod === 'both') && !validateEmail(recipientEmail)) {
          toast.error('Please enter a valid borrower email address');
          return false;
        }
        if ((deliveryMethod === 'sms' || deliveryMethod === 'both') && !validatePhone(recipientPhone)) {
          toast.error('Please enter a valid borrower phone number');
          return false;
        }

        // Validate each active partner's contact info against its chosen delivery method
        for (const p of activeSelectedPartners) {
          const method = partnerDeliveryMethods[p.id];
          if ((method === 'email' || method === 'both') && !p.email) {
            toast.error(`${p.firstName} ${p.lastName} has no email on file`);
            return false;
          }
          if ((method === 'sms' || method === 'both') && !p.mobilePhone) {
            toast.error(`${p.firstName} ${p.lastName} has no phone on file`);
            return false;
          }
        }

        // Validate co-borrower contact info against chosen delivery method
        if (sendToCoBorrower && selectedCoBorrower) {
          if ((coBorrowerDeliveryMethod === 'email' || coBorrowerDeliveryMethod === 'both') && !validateEmail(selectedCoBorrower.email || '')) {
            toast.error('Selected co-borrower does not have a valid email address');
            return false;
          }
          if ((coBorrowerDeliveryMethod === 'sms' || coBorrowerDeliveryMethod === 'both') && !validatePhone(selectedCoBorrower.mobilePhone || '')) {
            toast.error('Selected co-borrower does not have a valid phone number');
            return false;
          }
        }
        return true;
      }

      case 3: {
        // Combined SMS consent gate: any non-partner SMS recipient requires consent acknowledgment.
        const borrowerUsesSms = deliveryMethod === 'sms' || deliveryMethod === 'both';
        const coBorrowerUsesSms = sendToCoBorrower && (coBorrowerDeliveryMethod === 'sms' || coBorrowerDeliveryMethod === 'both');
        const anyNonPartnerSms = borrowerUsesSms || coBorrowerUsesSms;
        if (anyNonPartnerSms && !smsConsent) {
          toast.error('Please confirm SMS consent');
          return false;
        }
        return true;
      }

      default:
        return true;
    }
  };

  const handleNext = () => {
    if (isEditingDisabled) {
      return;
    }

    if (!validateStep(currentStep)) {
      return;
    }

    if (currentStep === 2) {
      if (step3GuardTimerRef.current) {
        clearTimeout(step3GuardTimerRef.current);
      }
      setIsStep3SendGuardActive(true);
      step3GuardTimerRef.current = setTimeout(() => {
        setIsStep3SendGuardActive(false);
        step3GuardTimerRef.current = null;
      }, 750);
    }

    setCurrentStep((prev) => Math.min(prev + 1, 3) as 1 | 2 | 3);
  };

  const handleBack = () => {
    if (isEditingDisabled) {
      return;
    }
    if (step3GuardTimerRef.current) {
      clearTimeout(step3GuardTimerRef.current);
      step3GuardTimerRef.current = null;
    }
    setIsStep3SendGuardActive(false);
    setCurrentStep((prev) => Math.max(prev - 1, 1) as 1 | 2 | 3);
  };

  const handleSavePartnerContact = async (partnerId: string) => {
    if (isEditingDisabled) {
      return;
    }

    const target = selectedPartners.find(p => p.id === partnerId);
    if (!target) {
      return;
    }

    // Validate at least one field is filled
    if (!editPartnerEmail.trim() && !editPartnerPhone.trim()) {
      toast.error('Please enter at least an email or phone number');
      return;
    }

    // Validate email format if provided
    if (editPartnerEmail.trim() && !validateEmail(editPartnerEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSavingPartnerContact(true);

    try {
      log.info('Saving partner contact info', {
        partnerId: target.id,
        hasEmail: Boolean(editPartnerEmail.trim()),
        hasPhone: Boolean(editPartnerPhone.trim())
      });

      // Update partner contact via service
      const updatedPartner = await partnerContactService.update(target.id, {
        email: editPartnerEmail.trim() || undefined,
        mobilePhone: editPartnerPhone.trim() || undefined
      });

      log.info('Partner contact info saved successfully');

      // Update partner entry in selectedPartners + regenerate default message if not dirty
      setSelectedPartners(prev => prev.map(p => p.id === target.id ? updatedPartner : p));
      if (!dirtyPartnerMessageIds.has(target.id)) {
        setPartnerMessages(prev => ({
          ...prev,
          [target.id]: buildPartnerMessage(preApproval, updatedPartner, loanOfficerName, loanOfficerTitle, loanOfficerPhone)
        }));
      }

      // Clear edit fields
      setEditPartnerEmail('');
      setEditPartnerPhone('');
      setEditingPartnerId(null);

      // Show success toast
      toast.success('Partner contact info updated successfully');

    } catch (error) {
      log.error('Failed to save partner contact info', {}, error as Error);
      toast.error('Failed to save contact info. Please try again.');
    } finally {
      setIsSavingPartnerContact(false);
    }
  };

  const handleSend = async () => {
    if (sendLockRef.current || isSending || isStep3SendGuardActive) {
      return;
    }

    sendLockRef.current = true;

    try {
      // Validate final step
      if (!validateStep(3)) {
        return;
      }

      // CRITICAL: Validate loan details before sending
      const loanAmount = preApproval?.loanDetails?.loanAmount || 0;
      const purchasePrice = preApproval?.loanDetails?.purchasePrice || 0;

      if (loanAmount <= 0 || purchasePrice <= 0) {
        toast.error(
          'Cannot send pre-approval with incomplete loan details. Please edit the pre-approval and enter valid loan amount and purchase price.',
          { duration: 5000 }
        );
        return;
      }

      if (purchasePrice < loanAmount) {
        toast.error(
          'Purchase price must be at least the loan amount. Please edit the pre-approval.',
          { duration: 5000 }
        );
        return;
      }

      setSendPhase('sending');
      setSendResults([]);

      const results: RecipientSendResult[] = [];
      const recordResult = (result: RecipientSendResult) => {
        results.push(result);
        setSendResults([...results]);
      };

      // Generate individual tokens for each recipient before any external send.
      const recipients: PreApprovalRecipient[] = [];
      const canonicalExpiresAt = getPreApprovalExpirationInstant(
        resolveCanonicalPreApprovalExpiresAt(preApproval.expiresAt, preApproval.expirationDate)
      ) || new Date(Date.now() + PRE_APPROVAL_CONSTANTS.EXPIRATION.SHARE_TOKEN_DAYS * 24 * 60 * 60 * 1000).toISOString();
      const borrowerName = `${preApproval.borrowerInfo.firstName} ${preApproval.borrowerInfo.lastName}`.trim();
      const borrowerRecipient = RecipientAccessService.generateRecipientToken(
        preApproval.id,
        'borrower',
        recipientEmail,
        borrowerName,
        canonicalExpiresAt
      );
      borrowerRecipient.phone = recipientPhone ? SmsService.formatToE164(recipientPhone) : null;
      recipients.push(borrowerRecipient);

      let coBorrowerRecipient: PreApprovalRecipient | undefined;
      if (sendToCoBorrower && selectedCoBorrower) {
        const coBorrowerName = `${selectedCoBorrower.firstName} ${selectedCoBorrower.lastName}`.trim();
        coBorrowerRecipient = RecipientAccessService.generateRecipientToken(
          preApproval.id,
          'co_borrower',
          selectedCoBorrower.email || '',
          coBorrowerName,
          canonicalExpiresAt
        );
        coBorrowerRecipient.phone = selectedCoBorrower.mobilePhone
          ? SmsService.formatToE164(selectedCoBorrower.mobilePhone)
          : null;
        recipients.push(coBorrowerRecipient);
      }

      const partnerRecipientByPartnerId = new Map<string, PreApprovalRecipient>();
      for (const partner of activeSelectedPartners) {
        const partnerName = `${partner.firstName} ${partner.lastName}`.trim();
        const partnerRecipient = RecipientAccessService.generateRecipientToken(
          preApproval.id,
          'partner',
          partner.email || '',
          partnerName,
          canonicalExpiresAt
        );
        partnerRecipient.phone = partner.mobilePhone
          ? SmsService.formatToE164(partner.mobilePhone)
          : null;
        recipients.push(partnerRecipient);
        partnerRecipientByPartnerId.set(partner.id, partnerRecipient);
      }

      await RecipientAccessService.addRecipientsToMetadata(preApproval.id, recipients);

      const recipientUrl = (recipient: PreApprovalRecipient) =>
        `${window.location.origin}/pre-approval/${recipient.token}`;
      const borrowerUrl = recipientUrl(borrowerRecipient);

      const attemptedRecipients = recipients.map(r => ({
        type: r.type,
        email: r.email,
        name: r.name,
        phone: r.phone || ''
      }));

      const addActivitySafely = async (
        description: string,
        metadata: Record<string, string | number | boolean>
      ) => {
        try {
          await SupabasePreApprovalService.addActivity(preApproval.id, {
            type: 'sent',
            description,
            metadata
          });
        } catch (activityError) {
          log.warn('Failed to write pre-approval send activity', {
            preApprovalId: preApproval.id,
            description,
            error: getErrorMessage(activityError)
          });
        }
      };

      await addActivitySafely('Pre-approval send attempted', {
        phase: 'attempted',
        deliveryMethod,
        partnerDeliveryMethods: activeSelectedPartners.length > 0
          ? activeSelectedPartners.map(p => `${p.id}:${partnerDeliveryMethods[p.id] || ''}`).join(',')
          : '',
        coBorrowerDeliveryMethod: sendToCoBorrower ? coBorrowerDeliveryMethod : '',
        ccEmails: ccEmails.join(', '),
        recipientCount: recipients.length,
        recipientTypes: recipients.map(r => r.type).join(', '),
        attemptedRecipientsJson: JSON.stringify(attemptedRecipients)
      });

      const sendEmailResult = async (
        recipientType: 'borrower' | 'co_borrower' | 'partner',
        recipientName: string,
        destination: string,
        emailMessage: string,
        accessUrl: string,
        cc?: string
      ) => {
        try {
          const emailResult = await EmailService.sendPreApprovalEmail(
            preApproval,
            destination,
            emailMessage,
            accessUrl,
            cc,
            { recipientType, recipientName }
          );

          if (!emailResult.success) {
            throw new Error(emailResult.error || 'Failed to send email');
          }

          recordResult({
            recipientType,
            recipientName,
            channel: 'email',
            destination,
            status: 'success',
            accessUrl
          });
          return true;
        } catch (emailError) {
          const errorMessage = getErrorMessage(emailError, 'Failed to send email');
          log.error('Failed to send pre-approval email', {
            recipientType,
            destination,
            preApprovalId: preApproval.id
          }, emailError as Error);
          recordResult({
            recipientType,
            recipientName,
            channel: 'email',
            destination,
            status: 'failed',
            accessUrl,
            error: errorMessage
          });
          return false;
        }
      };

      const loadSmsSender = async () => {
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('business_sms_number, first_name, last_name')
          .eq('id', user?.id)
          .single();

        if (userError || !userData) {
          throw new Error('Failed to load user information');
        }

        if (!userData.business_sms_number) {
          throw new Error('Please assign a Twilio SMS number in your profile settings first.');
        }

        return {
          id: user?.id || '',
          businessSmsNumber: userData.business_sms_number,
          firstName: userData.first_name,
          lastName: userData.last_name
        };
      };

      const sendSmsResult = async (
        recipientType: 'borrower' | 'partner' | 'co_borrower',
        recipientName: string,
        destination: string,
        smsMessage: string,
        accessUrl: string
      ) => {
        try {
          const smsSender = await loadSmsSender();

          // Audit-truth gate: only record consent for phones that don't already have active consent.
          // Skip for partners (they're TCPA-exempt per server policy).
          if (recipientType !== 'partner') {
            const phoneE164 = SmsService.formatToE164(destination);
            const consent = await SmsService.checkSmsConsentByPhone(phoneE164);
            if (smsConsent && !consent.granted) {
              const { error: consentError } = await supabase.rpc('record_sms_consent_with_phone', {
                p_borrower_id: recipientType === 'borrower' ? (preApproval?.borrowerInfo?.id || null) : null,
                p_borrower_phone: phoneE164,
                p_method: 'pre_approval_send',
                p_expires_days: 90
              });

              if (consentError) {
                log.warn('Failed to record SMS consent', { error: consentError });
              }
            }
          }

          const smsResult = await SmsService.sendPreApprovalSMS(
            preApproval,
            destination,
            smsMessage,
            accessUrl,
            smsSender,
            recipientType
          );

          if (!smsResult.success) {
            throw new Error(smsResult.error || 'Failed to send SMS');
          }

          recordResult({
            recipientType,
            recipientName,
            channel: 'sms',
            destination,
            status: 'success',
            accessUrl
          });
          return true;
        } catch (smsError) {
          const errorMessage = getErrorMessage(smsError, 'Failed to send SMS');
          log.error('Failed to send pre-approval SMS', {
            recipientType,
            destination,
            preApprovalId: preApproval.id
          }, smsError as Error);
          recordResult({
            recipientType,
            recipientName,
            channel: 'sms',
            destination,
            status: 'failed',
            accessUrl,
            error: errorMessage
          });
          return false;
        }
      };

      if (deliveryMethod === 'email' || deliveryMethod === 'both') {
        const borrowerEmailSucceeded = await sendEmailResult(
          'borrower',
          borrowerName,
          recipientEmail,
          message,
          borrowerUrl,
          ccEmails.length > 0 ? ccEmails.join(', ') : undefined
        );

        if (borrowerEmailSucceeded) {
          try {
            const confirmationResult = await EmailService.sendLoanOfficerConfirmation(preApproval, recipientEmail);
            if (!confirmationResult.success) {
              throw new Error(confirmationResult.error || 'Failed to send loan officer confirmation');
            }
            recordResult({
              recipientType: 'loan_officer',
              recipientName: loanOfficerName,
              channel: 'confirmation',
              destination: user?.email || 'Loan officer',
              status: 'success',
              accessUrl: `${window.location.origin}/pre-approval/${preApproval.id}/preview?lo=true`
            });
          } catch (confirmationError) {
            recordResult({
              recipientType: 'loan_officer',
              recipientName: loanOfficerName,
              channel: 'confirmation',
              destination: user?.email || 'Loan officer',
              status: 'failed',
              accessUrl: `${window.location.origin}/pre-approval/${preApproval.id}/preview?lo=true`,
              error: getErrorMessage(confirmationError, 'Failed to send loan officer confirmation')
            });
          }
        }
      }

      if (deliveryMethod === 'sms' || deliveryMethod === 'both') {
        await sendSmsResult('borrower', borrowerName, recipientPhone, message, borrowerUrl);
      }

      if (sendToCoBorrower && selectedCoBorrower && coBorrowerRecipient) {
        const coUrl = recipientUrl(coBorrowerRecipient);
        if (coBorrowerDeliveryMethod === 'email' || coBorrowerDeliveryMethod === 'both') {
          await sendEmailResult(
            'co_borrower',
            coBorrowerRecipient.name,
            selectedCoBorrower.email || '',
            coBorrowerMessage,
            coUrl
          );
        }
        if (coBorrowerDeliveryMethod === 'sms' || coBorrowerDeliveryMethod === 'both') {
          await sendSmsResult(
            'co_borrower',
            coBorrowerRecipient.name,
            selectedCoBorrower.mobilePhone || '',
            coBorrowerMessage,
            coUrl
          );
        }
      }

      for (const partner of activeSelectedPartners) {
        const partnerRecipient = partnerRecipientByPartnerId.get(partner.id);
        if (!partnerRecipient) {
          continue;
        }
        const partnerUrl = recipientUrl(partnerRecipient);
        const method = partnerDeliveryMethods[partner.id];
        const body = partnerMessages[partner.id] || buildPartnerMessage(preApproval, partner, loanOfficerName, loanOfficerTitle, loanOfficerPhone);

        if (method === 'email' || method === 'both') {
          await sendEmailResult(
            'partner',
            partnerRecipient.name,
            partner.email || '',
            body,
            partnerUrl
          );
        }
        if (method === 'sms' || method === 'both') {
          await sendSmsResult(
            'partner',
            partnerRecipient.name,
            partner.mobilePhone || '',
            body,
            partnerUrl
          );
        }
      }

      const externalResults = results.filter(result => result.recipientType !== 'loan_officer');
      const successfulExternalResults = externalResults.filter(result => result.status === 'success');
      const hasSuccessfulExternalSend = successfulExternalResults.length > 0;
      const borrowerSucceeded = results.some(result =>
        result.recipientType === 'borrower' && result.status === 'success'
      );

      if (borrowerSucceeded) {
        try {
          await navigator.clipboard.writeText(borrowerUrl);
        } catch (clipboardError) {
          log.warn('Failed to copy pre-approval link to clipboard', {
            preApprovalId: preApproval.id,
            error: getErrorMessage(clipboardError)
          });
        }
      }

      if (hasSuccessfulExternalSend) {
        const sentAt = new Date().toISOString();
        const hasSignature = preApproval?.hasLoanOfficerSignature || false;
        const signatureRecipientHistory = hasSignature ? {
          signatureSendCount: (preApproval.signatureSendCount || 0) + 1,
          lastSentWithSignatureAt: sentAt,
          signatureRecipientHistory: [
            ...(preApproval.signatureRecipientHistory || []),
            {
              sent_at: sentAt,
              recipients: successfulExternalResults.map(result => ({
                type: result.recipientType,
                destination: result.destination,
                name: result.recipientName,
                channel: result.channel
              })),
              signature_included: true
            }
          ]
        } : {};

        await SupabasePreApprovalService.updatePreApproval(preApproval.id, {
          status: 'sent',
          expiresAt: canonicalExpiresAt,
          lastSentAt: sentAt,
          accessLink: {
            token: borrowerRecipient.token,
            url: borrowerUrl,
            expiresAt: borrowerRecipient.tokenExpiresAt,
            lastAccessed: null
          },
          ...(sendToCoBorrower && selectedCoBorrower ? {
            coBorrowerInfo: {
              salutation: selectedCoBorrower.salutation || '',
              firstName: selectedCoBorrower.firstName,
              lastName: selectedCoBorrower.lastName,
              email: selectedCoBorrower.email || '',
              phone: selectedCoBorrower.mobilePhone || ''
            }
          } : {}),
          ...signatureRecipientHistory
        });
      }

      await addActivitySafely('Pre-approval send completed', {
        phase: 'completed',
        deliveryMethod,
        successCount: successfulExternalResults.length,
        failureCount: externalResults.length - successfulExternalResults.length,
        loanOfficerConfirmationStatus: results.find(result => result.recipientType === 'loan_officer')?.status || '',
        resultsJson: JSON.stringify(results.map(result => ({
          recipientType: result.recipientType,
          recipientName: result.recipientName,
          channel: result.channel,
          destination: result.destination,
          status: result.status,
          error: result.error || ''
        })))
      });

      setSendResults([...results]);
      setSendPhase(hasSuccessfulExternalSend ? 'sent' : 'failed');
    } catch (error) {
      log.error('Failed to send pre-approval', { preApprovalId: preApproval.id }, error as Error);
      setSendPhase('failed');
      toast.error('Failed to send pre-approval. Please try again.');
    } finally {
      sendLockRef.current = false;
    }
  };

  const handleDone = () => {
    const hasSuccessfulExternalSend = sendResults.some(result =>
      result.recipientType !== 'loan_officer' && result.status === 'success'
    );

    if (hasSuccessfulExternalSend) {
      onSend();
    }

    onClose();
  };

  return (
    <>
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isSending) {
          onClose();
        }
      }}
    >
      <DialogContent
        className={`max-h-[90vh] flex flex-col ${
          currentStep === 3
            ? (activeSelectedPartners.length > 1 ? 'max-w-5xl' : sendToPartner ? 'max-w-4xl' : 'max-w-xl')
            : 'max-w-xl'
        }`}
        hideCloseButton={isSending || isResultPhase}
        onEscapeKeyDown={(event) => {
          if (currentStep === 3 || isSending || isResultPhase) {
            event.preventDefault();
          }
        }}
        onInteractOutside={(event) => {
          if (currentStep === 3 || isSending || isResultPhase) {
            event.preventDefault();
          }
        }}
      >
        <DialogHeader className="pb-2">
          <DialogTitle>{isResend ? 'Resend' : 'Send'} Pre-Approval Letter</DialogTitle>
          <DialogDescription>
            {preApproval.borrowerInfo.firstName} {preApproval.borrowerInfo.lastName}
          </DialogDescription>
        </DialogHeader>

        {!isResultPhase && (
          <div className="flex items-center justify-center gap-2 py-2">
            <div className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-semibold transition-colors ${
              currentStep >= 1 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              {currentStep > 1 ? <Check className="w-4 h-4" /> : '1'}
            </div>
            <div className={`w-12 h-0.5 transition-colors ${currentStep >= 2 ? 'bg-primary' : 'bg-gray-200'}`} />
            <div className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-semibold transition-colors ${
              currentStep >= 2 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              {currentStep > 2 ? <Check className="w-4 h-4" /> : '2'}
            </div>
            <div className={`w-12 h-0.5 transition-colors ${currentStep >= 3 ? 'bg-primary' : 'bg-gray-200'}`} />
            <div className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-semibold transition-colors ${
              currentStep >= 3 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              3
            </div>
            <span className="ml-2 text-sm text-muted-foreground">Step {currentStep} of 3</span>
          </div>
        )}

        <div className="py-6 overflow-y-auto flex-1">
          {isResultPhase && (
            <div className="space-y-5">
              <div className="text-center">
                <h3 className="text-lg font-semibold mb-1">
                  {sendPhase === 'sent' ? 'Send complete' : 'Send failed'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  Review exactly which delivery attempts succeeded or failed.
                </p>
              </div>

              <div className="space-y-2">
                {sendResults.length === 0 ? (
                  <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    No deliveries were completed. Please try again.
                  </div>
                ) : (
                  sendResults.map((result, index) => (
                    <div
                      key={`${result.recipientType}-${result.channel}-${result.destination}-${index}`}
                      className={`rounded-md border p-3 text-sm ${
                        result.status === 'success'
                          ? 'border-green-200 bg-green-50'
                          : 'border-red-200 bg-red-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-medium">{result.recipientName}</div>
                          <div className="text-xs text-muted-foreground">
                            {result.recipientType === 'co_borrower' ? 'Co-Borrower' :
                              result.recipientType === 'loan_officer' ? 'Loan Officer Confirmation' :
                              result.recipientType === 'partner' ? 'Partner' : 'Borrower'} · {result.channel}
                          </div>
                          <div className="text-xs text-muted-foreground">{result.destination}</div>
                          {result.error && (
                            <div className="mt-1 text-xs text-red-700">{result.error}</div>
                          )}
                        </div>
                        <span className={`rounded-full px-2 py-1 text-xs font-medium ${
                          result.status === 'success'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {result.status === 'success' ? 'Sent' : 'Failed'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Step 1: Recipients */}
          {!isResultPhase && currentStep === 1 && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold mb-1">Who should receive this?</h3>
                <p className="text-sm text-muted-foreground">Select recipients for the pre-approval letter</p>
              </div>

              {/* Borrower — always included. Uses a static icon instead of a disabled
                  Radix checkbox because Radix selection controls are <button>-based
                  and the global disabled-button rule (index.css) forces
                  background-color: transparent, which hides the checkmark. */}
              <div className="p-4 border border-blue-200 rounded-lg bg-blue-50/50">
                <div className="flex items-start gap-3">
                  <div className="mt-1 h-4 w-4 shrink-0 rounded-sm bg-blue-600 flex items-center justify-center">
                    <Check className="h-3 w-3 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Mail className="w-4 h-4 text-blue-600" />
                      <Label className="font-semibold">{preApproval.borrowerInfo.firstName} {preApproval.borrowerInfo.lastName} (Borrower)</Label>
                      <span className="text-xs text-blue-600 font-medium">(required)</span>
                    </div>
                    <div className="text-sm text-muted-foreground space-y-0.5">
                      {recipientEmail && <div>{recipientEmail}</div>}
                      {recipientPhone && <div>{formatPhoneNumber(recipientPhone)}</div>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Included automatically</p>
                  </div>
                </div>
              </div>

              {/* Partner Option */}
              <div className={`p-4 border rounded-lg transition-colors ${sendToPartner ? 'bg-green-50/50 border-green-200' : 'bg-gray-50/50'}`}>
                <div className="flex items-start gap-3 mb-3">
                  <Checkbox
                    id="send-to-partner"
                    checked={sendToPartner}
                    disabled={isEditingDisabled}
                    onCheckedChange={(checked) => {
                      if (isEditingDisabled) {
                        return;
                      }
                      setSendToPartner(checked as boolean);
                    }}
                    className="mt-1"
                  />
                  <Label htmlFor="send-to-partner" className="font-semibold cursor-pointer">Also send to partner/realtor</Label>
                </div>

                {sendToPartner && (
                  <div className="ml-8 space-y-2">
                    <Label className="text-sm">Select Partners</Label>
                    {partners.length === 0 ? (
                      <div className="p-2 text-sm text-muted-foreground border rounded">
                        No partners found. Add a partner/realtor in{' '}
                        <a
                          href="/partners"
                          className="text-blue-600 underline underline-offset-2 hover:text-blue-800"
                        >
                          Co-Marketing Partners
                        </a>{' '}
                        first.
                      </div>
                    ) : (
                      <div className="max-h-56 overflow-y-auto border rounded p-2 space-y-1">
                        {partners.map((partner) => {
                          const isSelected = selectedPartners.some(p => p.id === partner.id);
                          return (
                            <div key={partner.id} className="flex items-center gap-2 px-1 py-1 hover:bg-gray-50 rounded">
                              <Checkbox
                                id={`partner-pick-${partner.id}`}
                                checked={isSelected}
                                disabled={isEditingDisabled}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    addSelectedPartner(partner);
                                  } else {
                                    removeSelectedPartner(partner.id);
                                  }
                                }}
                              />
                              <Label htmlFor={`partner-pick-${partner.id}`} className="text-sm cursor-pointer flex-1">
                                {partner.firstName} {partner.lastName}
                                <span className="text-muted-foreground"> — {formatPartnerType(partner.partnerType)}</span>
                              </Label>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full mt-2 text-sm"
                      onClick={() => setShowQuickAddPartner(true)}
                      disabled={isEditingDisabled}
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add New Partner
                    </Button>

                    {selectedPartners.map((partner) => (
                      <div key={`pick-detail-${partner.id}`} className="mt-2 space-y-2">
                        <div className="p-2 bg-white rounded border text-xs text-muted-foreground">
                          <div className="font-medium text-gray-900 mb-1">{partner.firstName} {partner.lastName}</div>
                          {partner.email && <div>✉️ {partner.email}</div>}
                          {partner.mobilePhone && <div>📱 {formatPhoneNumber(partner.mobilePhone)}</div>}
                          {!partner.email && !partner.mobilePhone && (
                            <div className="text-red-500 font-medium">⚠️ No contact information on file</div>
                          )}
                        </div>

                        {/* Inline edit for missing contact info */}
                        {(!partner.email || !partner.mobilePhone) && editingPartnerId === partner.id && (
                          <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs">
                            <div className="flex items-center gap-1 text-amber-800 mb-2">
                              <AlertCircle className="w-3 h-3" />
                              <span className="font-medium">Update Contact Information</span>
                            </div>

                            <div className="space-y-2 mb-3">
                              {!partner.email && (
                                <div>
                                  <Label htmlFor={`edit-partner-email-${partner.id}`} className="text-xs text-amber-900 mb-1 block">
                                    Email Address
                                  </Label>
                                  <Input
                                    id={`edit-partner-email-${partner.id}`}
                                    type="email"
                                    value={editPartnerEmail}
                                    onChange={(e) => setEditPartnerEmail(e.target.value)}
                                    placeholder="partner@example.com"
                                    className="h-8 text-xs"
                                    disabled={isSavingPartnerContact || isEditingDisabled}
                                    autoComplete="off"
                                  />
                                </div>
                              )}
                              {!partner.mobilePhone && (
                                <div>
                                  <Label htmlFor={`edit-partner-phone-${partner.id}`} className="text-xs text-amber-900 mb-1 block">
                                    Mobile Phone
                                  </Label>
                                  <Input
                                    id={`edit-partner-phone-${partner.id}`}
                                    type="tel"
                                    value={editPartnerPhone}
                                    onChange={(e) => {
                                      const cleaned = e.target.value.replace(/[^\d\s\-()]/g, '');
                                      setEditPartnerPhone(cleaned);
                                    }}
                                    placeholder="(555) 555-5555"
                                    className="h-8 text-xs"
                                    disabled={isSavingPartnerContact || isEditingDisabled}
                                    autoComplete="off"
                                  />
                                </div>
                              )}
                            </div>

                            <Button
                              type="button"
                              size="sm"
                              onClick={() => handleSavePartnerContact(partner.id)}
                              disabled={isEditingDisabled || isSavingPartnerContact || (!editPartnerEmail.trim() && !editPartnerPhone.trim())}
                              className="w-full h-8 text-xs"
                            >
                              {isSavingPartnerContact ? 'Saving...' : 'Save Contact Info'}
                            </Button>
                          </div>
                        )}
                        {(!partner.email || !partner.mobilePhone) && editingPartnerId !== partner.id && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="w-full h-7 text-xs"
                            onClick={() => setEditingPartnerId(partner.id)}
                            disabled={isEditingDisabled}
                          >
                            Add missing {!partner.email ? 'email' : ''}{!partner.email && !partner.mobilePhone ? ' or ' : ''}{!partner.mobilePhone ? 'phone' : ''}
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Co-Borrower Option */}
              <div className={`p-4 border rounded-lg transition-colors ${sendToCoBorrower ? 'bg-purple-50/50 border-purple-200' : 'bg-gray-50/50'}`}>
                <div className="flex items-start gap-3 mb-3">
                  <Checkbox
                    checked={sendToCoBorrower}
                    disabled={isEditingDisabled}
                    onCheckedChange={(checked) => {
                      if (isEditingDisabled) {
                        return;
                      }
                      setSendToCoBorrower(checked as boolean);
                      if (!checked) {
                        setSelectedCoBorrower(null);
                      }
                    }}
                    className="mt-1"
                  />
                  <Label className="font-semibold cursor-pointer">Also send to co-borrower</Label>
                </div>

                {sendToCoBorrower && (
                  <div className="ml-8 space-y-2">
                    <Label className="text-sm">Select Co-Borrower</Label>
                    <Select
                      value={selectedCoBorrower?.id || ''}
                      disabled={isEditingDisabled}
                      onValueChange={(value) => {
                        const coBorrower = borrowers.find(b => b.id === value);
                        setSelectedCoBorrower(coBorrower || null);
                        if (!dirtyMessages.coBorrower) {
                          setCoBorrowerMessage(buildCoBorrowerMessage(preApproval, coBorrower || null, loanOfficerName));
                        }
                      }}
                    >
                      <SelectTrigger className="text-sm">
                        <SelectValue placeholder="Choose a co-borrower..." />
                      </SelectTrigger>
                      <SelectContent>
                        {borrowers.length === 0 ? (
                          <div className="p-2 text-sm text-muted-foreground">No borrowers found</div>
                        ) : (
                          borrowers.map((borrower) => (
                            <SelectItem key={borrower.id} value={borrower.id} className="text-sm">
                              {borrower.firstName} {borrower.lastName}
                              {borrower.source === 'pre_approval' && (
                                <span className="text-muted-foreground ml-1">(from this pre-approval)</span>
                              )}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>

                    {selectedCoBorrower && (
                      <div className="mt-2 p-2 bg-white rounded border text-xs text-muted-foreground">
                        {selectedCoBorrower.email && <div>✉️ {selectedCoBorrower.email}</div>}
                        {selectedCoBorrower.mobilePhone && <div>📱 {formatPhoneNumber(selectedCoBorrower.mobilePhone)}</div>}
                        {!selectedCoBorrower.email && !selectedCoBorrower.mobilePhone && (
                          <div className="text-amber-600 font-medium">⚠️ No contact info on file — co-borrower cannot receive the letter</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Pre-Approval Summary */}
              <div className="mt-4 p-3 bg-gray-50 rounded-lg border">
                <div className="text-xs font-medium text-muted-foreground mb-2">Pre-Approval Summary</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Loan: ${preApproval.loanDetails.loanAmount?.toLocaleString() || '0'}</div>
                  <div>Price: ${preApproval.loanDetails.purchasePrice?.toLocaleString() || '0'}</div>
                  <div>Down: {formatPercentage(preApproval.loanDetails.downPaymentPercent || 0, 1)}</div>
                  <div>Type: {formatLoanType(preApproval.loanDetails.loanType || 'N/A')}</div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Delivery Method */}
          {!isResultPhase && currentStep === 2 && (
            <div className="space-y-5">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold mb-1">How should we send?</h3>
                <p className="text-sm text-muted-foreground">Choose delivery method for each recipient</p>
              </div>

              {/* Borrower Delivery */}
              <div>
                <Label className="font-semibold mb-3 block">Borrower</Label>
                <RadioGroup
                  value={deliveryMethod}
                  disabled={isEditingDisabled}
                  onValueChange={(value) => setDeliveryMethod(value as 'email' | 'sms' | 'both')}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="email" id="b-email" />
                    <Label htmlFor="b-email" className="flex items-center cursor-pointer text-sm">
                      <Mail className="w-4 h-4 mr-2" />
                      Email
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sms" id="b-sms" />
                    <Label htmlFor="b-sms" className="flex items-center cursor-pointer text-sm">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      SMS Text
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="both" id="b-both" />
                    <Label htmlFor="b-both" className="flex items-center cursor-pointer text-sm">
                      <Mail className="w-4 h-4 mr-2" />
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Both
                    </Label>
                  </div>
                </RadioGroup>

                <div className="mt-3 p-2 bg-blue-50 rounded text-sm text-muted-foreground">
                  {(deliveryMethod === 'email' || deliveryMethod === 'both') && <div>✓ {recipientEmail}</div>}
                  {(deliveryMethod === 'sms' || deliveryMethod === 'both') && <div>✓ {formatPhoneNumber(recipientPhone)}</div>}
                </div>
              </div>

              {/* Partner Delivery — one section per selected partner */}
              {activeSelectedPartners.map((partner) => {
                const hasPartnerEmail = Boolean(partner.email);
                const hasPartnerPhone = Boolean(partner.mobilePhone);
                const method = partnerDeliveryMethods[partner.id] || 'email';
                const idPrefix = `p-${partner.id}`;
                return (
                  <div key={`partner-delivery-${partner.id}`} className="border-t pt-4">
                    <Label className="font-semibold mb-3 block">
                      {partner.firstName} {partner.lastName} - {formatPartnerType(partner.partnerType)}
                    </Label>
                    <RadioGroup
                      value={method}
                      disabled={isEditingDisabled}
                      onValueChange={(value) => setPartnerDeliveryMethods(prev => ({
                        ...prev,
                        [partner.id]: value as 'email' | 'sms' | 'both'
                      }))}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="email" id={`${idPrefix}-email`} disabled={!hasPartnerEmail} />
                        <Label htmlFor={`${idPrefix}-email`} className={`flex items-center text-sm ${!hasPartnerEmail ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                          <Mail className="w-4 h-4 mr-2" />
                          Email
                          {!hasPartnerEmail && <span className="ml-2 text-xs text-red-500">(No email on file)</span>}
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="sms" id={`${idPrefix}-sms`} disabled={!hasPartnerPhone} />
                        <Label htmlFor={`${idPrefix}-sms`} className={`flex items-center text-sm ${!hasPartnerPhone ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                          <MessageSquare className="w-4 h-4 mr-2" />
                          SMS Text
                          {!hasPartnerPhone && <span className="ml-2 text-xs text-red-500">(No phone on file)</span>}
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="both" id={`${idPrefix}-both`} disabled={!hasPartnerEmail || !hasPartnerPhone} />
                        <Label htmlFor={`${idPrefix}-both`} className={`flex items-center text-sm ${(!hasPartnerEmail || !hasPartnerPhone) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                          <Mail className="w-4 h-4 mr-2" />
                          <MessageSquare className="w-4 h-4 mr-2" />
                          Both
                        </Label>
                      </div>
                    </RadioGroup>

                    <div className="mt-3 p-2 bg-green-50 rounded text-sm text-muted-foreground">
                      {(method === 'email' || method === 'both') && partner.email && <div>✓ {partner.email}</div>}
                      {(method === 'sms' || method === 'both') && partner.mobilePhone && <div>✓ {formatPhoneNumber(partner.mobilePhone)}</div>}
                    </div>
                  </div>
                );
              })}

              {/* Co-Borrower Delivery — email/SMS/both like partner */}
              {sendToCoBorrower && selectedCoBorrower && (() => {
                const hasEmail = Boolean(selectedCoBorrower.email);
                const hasPhone = Boolean(selectedCoBorrower.mobilePhone);
                return (
                  <div className="border-t pt-4">
                    <Label className="font-semibold mb-3 block">
                      {selectedCoBorrower.firstName} {selectedCoBorrower.lastName} (Co-Borrower)
                    </Label>
                    <RadioGroup
                      value={coBorrowerDeliveryMethod}
                      disabled={isEditingDisabled}
                      onValueChange={(value) => setCoBorrowerDeliveryMethod(value as 'email' | 'sms' | 'both')}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="email" id="cb-email" disabled={!hasEmail} />
                        <Label htmlFor="cb-email" className={`flex items-center text-sm ${!hasEmail ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                          <Mail className="w-4 h-4 mr-2" />
                          Email
                          {!hasEmail && <span className="ml-2 text-xs text-red-500">(No email on file)</span>}
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="sms" id="cb-sms" disabled={!hasPhone} />
                        <Label htmlFor="cb-sms" className={`flex items-center text-sm ${!hasPhone ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                          <MessageSquare className="w-4 h-4 mr-2" />
                          SMS Text
                          {!hasPhone && <span className="ml-2 text-xs text-red-500">(No phone on file)</span>}
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="both" id="cb-both" disabled={!hasEmail || !hasPhone} />
                        <Label htmlFor="cb-both" className={`flex items-center text-sm ${(!hasEmail || !hasPhone) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                          <Mail className="w-4 h-4 mr-2" />
                          <MessageSquare className="w-4 h-4 mr-2" />
                          Both
                        </Label>
                      </div>
                    </RadioGroup>

                    <div className="mt-3 p-2 bg-purple-50 rounded text-sm text-muted-foreground">
                      {(coBorrowerDeliveryMethod === 'email' || coBorrowerDeliveryMethod === 'both') && selectedCoBorrower.email && <div>✓ {selectedCoBorrower.email}</div>}
                      {(coBorrowerDeliveryMethod === 'sms' || coBorrowerDeliveryMethod === 'both') && selectedCoBorrower.mobilePhone && <div>✓ {formatPhoneNumber(selectedCoBorrower.mobilePhone)}</div>}
                    </div>

                    {!hasEmail && !hasPhone && (
                      <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-amber-700">No contact info on file — co-borrower cannot receive the letter</span>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Step 3: Customize and Send */}
          {!isResultPhase && currentStep === 3 && (
            <div>
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold mb-1">Add a personal message</h3>
                <p className="text-sm text-muted-foreground">Customize your message and confirm consent</p>
              </div>

              <div className="mb-5 rounded-lg border bg-gray-50 p-3">
                <div className="mb-2 text-xs font-medium uppercase text-muted-foreground">Review Recipients</div>
                <div className="space-y-2">
                  {recipientReviewItems.map((item) => (
                    <div key={item.key} className="flex items-start justify-between gap-3 rounded-md bg-white p-2 text-sm">
                      <div>
                        <div className="font-medium">{item.name}</div>
                        <div className="text-xs text-muted-foreground">{item.role}</div>
                      </div>
                      <div className="text-right text-xs text-muted-foreground">
                        {item.methods.length > 0 ? item.methods.map((method) => (
                          <div key={method}>{method}</div>
                        )) : (
                          <div>No delivery method selected</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {(() => {
                const borrowerUsesSms = deliveryMethod === 'sms' || deliveryMethod === 'both';
                const coBorrowerUsesSms = sendToCoBorrower && (coBorrowerDeliveryMethod === 'sms' || coBorrowerDeliveryMethod === 'both');
                const anyNonPartnerSms = borrowerUsesSms || coBorrowerUsesSms;
                const consentLabel =
                  borrowerUsesSms && coBorrowerUsesSms
                    ? 'I have obtained SMS consent from the borrower and co-borrower'
                    : coBorrowerUsesSms
                      ? 'I have obtained SMS consent from the co-borrower'
                      : 'I have obtained SMS consent from the borrower';
                return (
                  <>
                    <div className={activeSelectedPartners.length > 0 ? 'grid grid-cols-1 md:grid-cols-2 gap-6' : 'space-y-4'}>
                      {/* Borrower Section */}
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="borrower-msg" className="text-sm font-semibold mb-2 block">Borrower Message</Label>
                          <Textarea
                            id="borrower-msg"
                            value={message}
                            onChange={(e) => {
                              setDirtyMessages(prev => ({ ...prev, borrower: true }));
                              setMessage(e.target.value);
                            }}
                            rows={5}
                            className="resize-none text-sm"
                            disabled={isEditingDisabled}
                          />
                        </div>

                        {/* Combined SMS Consent (covers borrower and co-borrower) */}
                        {anyNonPartnerSms && (
                          <div className="flex items-start space-x-2 p-3 bg-amber-50 border border-amber-200 rounded">
                            <Checkbox
                              id="b-consent"
                              checked={smsConsent}
                              disabled={isEditingDisabled}
                              onCheckedChange={(checked) => {
                                smsConsentManuallyEditedRef.current = true;
                                setSmsConsentManuallyEdited(true);
                                setSmsConsent(checked as boolean);
                              }}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <Label htmlFor="b-consent" className="text-xs cursor-pointer">
                                {consentLabel}
                              </Label>
                              <p className="text-xs text-amber-700 mt-1 flex items-center">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                Required by TCPA
                              </p>
                            </div>
                          </div>
                        )}

                        {/* CC Emails (only for email delivery) */}
                        {(deliveryMethod === 'email' || deliveryMethod === 'both') && (
                          <div>
                            <div className="mb-2 flex items-center gap-2">
                              <Label htmlFor="cc-emails" className="text-sm">Borrower email CC</Label>
                              <TooltipProvider delayDuration={200}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button type="button" className="text-xs text-muted-foreground underline underline-offset-2">
                                      Details
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="max-w-xs">
                                    CCs receive the borrower email and borrower link. They are not tracked recipients.
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                            <EmailTagInput
                              id="cc-emails"
                              value={ccEmails}
                              onChange={setCcEmails}
                              placeholder="Type email and press Enter"
                              disabled={isEditingDisabled}
                            />
                          </div>
                        )}
                      </div>

                      {/* Co-Borrower Section */}
                      {sendToCoBorrower && selectedCoBorrower && (
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="co-borrower-msg" className="text-sm font-semibold mb-2 block">
                              Co-Borrower Message
                              <span className="text-xs font-normal text-muted-foreground ml-2">
                                ({selectedCoBorrower.firstName} {selectedCoBorrower.lastName})
                              </span>
                            </Label>
                            <Textarea
                              id="co-borrower-msg"
                              value={coBorrowerMessage}
                              onChange={(e) => {
                                setDirtyMessages(prev => ({ ...prev, coBorrower: true }));
                                setCoBorrowerMessage(e.target.value);
                              }}
                              rows={5}
                              className="resize-none text-sm"
                              disabled={isEditingDisabled}
                            />
                          </div>
                        </div>
                      )}

                      {/* Partner Sections — one textarea per selected partner */}
                      {activeSelectedPartners.map((partner) => (
                        <div key={`partner-msg-${partner.id}`} className="space-y-4">
                          <div>
                            <Label htmlFor={`partner-msg-${partner.id}`} className="text-sm font-semibold mb-2 block">
                              Partner Message
                              <span className="text-xs font-normal text-muted-foreground ml-2">
                                ({partner.firstName} {partner.lastName})
                              </span>
                            </Label>
                            <Textarea
                              id={`partner-msg-${partner.id}`}
                              value={partnerMessages[partner.id] || ''}
                              onChange={(e) => {
                                setDirtyPartnerMessageIds(prev => new Set(prev).add(partner.id));
                                setPartnerMessages(prev => ({ ...prev, [partner.id]: e.target.value }));
                              }}
                              rows={5}
                              className="resize-none text-sm"
                              disabled={isEditingDisabled}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          <div className="flex justify-between w-full">
            <div>
              {!isResultPhase && currentStep > 1 && (
                <Button type="button" variant="outline" onClick={handleBack} disabled={isEditingDisabled} size="sm">
                  ← Back
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {isResultPhase ? (
                <Button type="button" onClick={handleDone} size="sm">
                  Done
                </Button>
              ) : (
                <>
                  <Button type="button" variant="outline" onClick={onClose} disabled={isSending} size="sm">
                    Cancel
                  </Button>
                  {currentStep < 3 ? (
                    <Button type="button" onClick={handleNext} disabled={isEditingDisabled} size="sm">
                      Next →
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      onClick={handleSend}
                      disabled={isSending || isStep3SendGuardActive}
                      size="sm"
                      className="bg-primary"
                    >
                      {isSending ? (
                        isResend ? 'Resending...' : 'Sending...'
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          {isResend ? 'Resend Letter' : 'Send Letter'}
                        </>
                      )}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>

      {/* Quick Add Partner Modal (renders as separate dialog layer) */}
      <QuickAddPartnerModal
        open={showQuickAddPartner}
        onClose={() => setShowQuickAddPartner(false)}
        onSuccess={async () => {
          await loadPartners();
        }}
        onPartnerCreated={(newPartner) => {
          setSendToPartner(true);
          addSelectedPartner(newPartner);
        }}
        mode="create"
      />
    </>
  );
};
